import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parseNumericInput } from '../utils/numberFormat.js'
import { generateRequestId } from '../utils/requestId.js'
import TenantSelectorModal from './TenantSelectorModal.jsx'
import ConnectionStatus from './ConnectionStatus.jsx'
import { t } from '../../i18n.js'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { handleHotKeyboardShortcut } from '../utils/hotKeyboardShortcuts.js'
import { showError } from '../utils/notifications.js'
import usePasteOverflow from '../hooks/usePasteOverflow.js'
import { buildEnumOptions } from '../utils/enumOptions.js'
import CapexSchedulerModal from './CapexSchedulerModal.jsx'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { HOT_DATE_FORMAT, HOT_DATE_LOCALE, hotNumericFormat } from '../utils/handsontableConfig.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

const DEBOUNCE_DELAY = 800

const CAPEX_TYPES = ['VALUE_PRESERVING', 'VALUE_ENHANCING', 'ESG_SUSTAINABILITY', 'OTHER']
const INPUT_METHODS = [
  'FIXED_AMOUNT', 'AMOUNT_PER_RENTABLE_AREA', 'AMOUNT_PER_VACANT_AREA',
  'PCT_RENTAL_REVENUE_BEFORE_RENT_FREE', 'PCT_RENTAL_REVENUE_AFTER_RENT_FREE', 'PCT_MARKET_RENT'
]
const PERCENT_METHODS = INPUT_METHODS.slice(3)
const AMOUNT_BASES = ['ANNUAL', 'MONTHLY']
const SCOPES = ['PROPERTY_LEVEL', 'EACH_TENANT', 'SELECTED_TENANTS']
const REPEATS = ['MONTHLY', 'QUARTERLY', 'ANNUALLY', 'SINGLE_PAYMENT']
const START_RULES = [
  'VALUATION_DATE', 'SPECIFIED_DATE', 'ON_CURRENT_VOIDS',
  'ON_FUTURE_VOIDS_AFTER_LEASE_EXPIRY', 'ON_ALL_VOIDS', 'ON_APPLIED_LEASE_END',
  'ON_RENT_ROLL_LEASE_START', 'ON_MLA_RELETTING_START',
  'ON_RENT_ROLL_LEASE_START_PLUS_MLA_RELETTING_START'
]
const VOID_START_RULES = ['ON_CURRENT_VOIDS', 'ON_FUTURE_VOIDS_AFTER_LEASE_EXPIRY', 'ON_ALL_VOIDS']
const TENANT_START_RULES = [
  'ON_APPLIED_LEASE_END', 'ON_RENT_ROLL_LEASE_START',
  'ON_MLA_RELETTING_START', 'ON_RENT_ROLL_LEASE_START_PLUS_MLA_RELETTING_START'
]
const END_RULES = ['NONE', 'SPECIFIED_DATE', 'FOR_NEXT_X_MONTHS', 'LEASE_EXPIRY']

// Fields locked while a scheduler is active (its phasing overrides timing).
const SCHEDULER_LOCKED_FIELDS = [
  'amount_basis', 'start_rule', 'specified_start_date', 'start_offset_months',
  'repeat', 'end_rule', 'specified_end_date', 'duration_months'
]

const COLUMNS = [
  { data: 'name', type: 'text' },
  // EVA-214: wide enough to keep the longest option ("ESG / Sustainability") on
  // one line — 140 wrapped "Value Preserving" and made the row two lines tall.
  { data: 'capex_type', type: 'dropdown', source: CAPEX_TYPES, width: 190 },
  { data: 'input_method', type: 'dropdown', source: INPUT_METHODS },
  { data: 'amount', type: 'numeric', ...hotNumericFormat(2, true) },
  { data: 'amount_basis', type: 'dropdown', source: AMOUNT_BASES },
  { data: 'calculation_scope', type: 'dropdown', source: SCOPES },
  { data: 'selected_tenants', type: 'text' },
  { data: 'start_rule', type: 'dropdown', source: START_RULES },
  { data: 'specified_start_date', type: 'date', locale: HOT_DATE_LOCALE, dateFormat: HOT_DATE_FORMAT },
  { data: 'start_offset_months', type: 'numeric' },
  { data: 'repeat', type: 'dropdown', source: REPEATS },
  { data: 'end_rule', type: 'dropdown', source: END_RULES },
  { data: 'specified_end_date', type: 'date', locale: HOT_DATE_LOCALE, dateFormat: HOT_DATE_FORMAT },
  { data: 'duration_months', type: 'numeric' },
  { data: 'growth_rate', type: 'dropdown', source: [], width: 160 }
]
const SELECTED_TENANTS_COL = COLUMNS.findIndex((c) => c.data === 'selected_tenants')
const AMOUNT_COL = COLUMNS.findIndex((c) => c.data === 'amount')
const NUMERIC_FIELDS = new Set(['amount', 'start_offset_months', 'duration_months'])

function isSchedulerEnabled(record) {
  return !!(record && record.scheduler && record.scheduler.enabled)
}

function isFieldDisabled(field, record) {
  if (isSchedulerEnabled(record) && SCHEDULER_LOCKED_FIELDS.includes(field)) return true
  switch (field) {
    case 'amount_basis':
    case 'growth_rate':
      return PERCENT_METHODS.includes(record.input_method)
    case 'selected_tenants':
      return record.calculation_scope !== 'SELECTED_TENANTS'
    case 'specified_start_date':
      return record.start_rule !== 'SPECIFIED_DATE'
    case 'specified_end_date':
      return record.end_rule !== 'SPECIFIED_DATE'
    case 'duration_months':
      return record.end_rule !== 'FOR_NEXT_X_MONTHS'
    case 'start_offset_months':
      return VOID_START_RULES.includes(record.start_rule)
    default:
      return false
  }
}

// Clear/coerce dependent fields after a rule change. Returns { field: value } of
// scalar clears to persist (scheduler clearing is handled separately).
function sanitizeRecord(record) {
  const cleared = {}
  const set = (field, value) => {
    const cur = record[field]
    const empty = (v) => v === null || v === undefined || (Array.isArray(v) && v.length === 0)
    if (cur === value || (empty(cur) && empty(value))) return
    record[field] = value
    cleared[field] = value
  }

  if (record.calculation_scope !== 'SELECTED_TENANTS') set('selected_tenants', [])
  if (record.start_rule !== 'SPECIFIED_DATE') set('specified_start_date', null)
  if (VOID_START_RULES.includes(record.start_rule)) set('start_offset_months', null)
  if (record.end_rule !== 'SPECIFIED_DATE') set('specified_end_date', null)
  if (record.end_rule !== 'FOR_NEXT_X_MONTHS') set('duration_months', null)
  if (PERCENT_METHODS.includes(record.input_method)) { set('amount_basis', null); set('growth_rate', null) }

  if (record.calculation_scope === 'PROPERTY_LEVEL') {
    if (TENANT_START_RULES.includes(record.start_rule)) set('start_rule', 'VALUATION_DATE')
    if (record.end_rule === 'LEASE_EXPIRY') set('end_rule', 'NONE')
  }

  return cleared
}


export default function CapitalExpenditureTab({
  valuationId, indexUrl, batchUpdateUrl, batchCreateUrl, createRowUrl, batchDestroyUrl,
  tenantsUrl, growthRateNames = [], growthProfiles = [], lengthOfAnalysisMonths = 0,
  valuationDate = null, optionLists = [], csrfToken, readOnly = false, translations
}) {
  // EVA-221: code⇄label bridge for the enum dropdowns (cells store codes, show labels).
  const enums = useMemo(() => buildEnumOptions(optionLists), [optionLists])
  const hotRef = useRef(null)
  const pendingRef = useRef(new Map())
  const flushTimeoutRef = useRef(null)
  const viewportRef = useRef(null)
  const [rawData, setRawData] = useState([])
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [hotHeight, setHotHeight] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('connecting')
  const [connectionMountEl, setConnectionMountEl] = useState(null)
  const [modalRow, setModalRow] = useState(null) // record id whose tenant modal is open
  const [schedulerRow, setSchedulerRow] = useState(null) // record id whose scheduler modal is open

  const schedulerYears = useMemo(
    () => Math.max(1, Math.ceil((Number(lengthOfAnalysisMonths || 0) + 12) / 12)),
    [lengthOfAnalysisMonths]
  )
  const growthProfileList = useMemo(() => {
    if (growthProfiles.length) return growthProfiles
    return (growthRateNames.length ? growthRateNames : ['Expense Inflation']).map((name) => ({ name, rates: {} }))
  }, [growthProfiles, growthRateNames])

  const tenantLabels = useMemo(() => {
    const map = new Map()
    tenants.forEach((tenant) => {
      const name = tenant.title || t('unnamed', translations)
      map.set(Number(tenant.id), tenant.suite ? `${name} / ${tenant.suite}` : name)
    })
    return map
  }, [tenants, translations])

  useEffect(() => {
    const container = document.querySelector('[data-react-component-component-value="CapitalExpenditureTab"]')
    const titleEl = container?.closest('.card')?.querySelector('.card-header h5.card-title')
    if (!titleEl) return
    const mount = document.createElement('span')
    titleEl.appendChild(mount)
    setConnectionMountEl(mount)
    return () => { setConnectionMountEl(null); mount.remove() }
  }, [])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const update = () => {
      const next = el.clientHeight
      setHotHeight((prev) => (prev === next ? prev : next))
      hotRef.current?.hotInstance?.refreshDimensions?.()
    }
    update()
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update())
      ro.observe(el)
    } else {
      window.addEventListener('resize', update)
    }
    return () => {
      if (ro) ro.disconnect()
      else window.removeEventListener('resize', update)
    }
  }, [loading])

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await fetch(indexUrl, { headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken } })
      const json = await res.json()
      if (json.success && json.data) setRawData(json.data)
    } catch (e) {
      console.error('Failed to fetch capital expenditures:', e)
    } finally {
      setLoading(false)
    }
  }, [indexUrl, csrfToken])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    if (!tenantsUrl) return
    fetch(tenantsUrl, { headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken } })
      .then((r) => r.json())
      .then((json) => { if (json.data) setTenants(json.data) })
      .catch((e) => console.error('Failed to fetch tenants:', e))
  }, [tenantsUrl, csrfToken])

  const { handleBeforePaste, creatingRowsRef } = usePasteOverflow({
    hotRef,
    enabled: !readOnly,
    createRows: async (count) => {
      const res = await fetch(batchCreateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          request_id: generateRequestId(),
          items: Array.from({ length: count }, (_, i) => ({ temp_id: `paste_${Date.now()}_${i}`, data: {} }))
        })
      })
      const json = await res.json()
      return json.success === true
    },
    refetchData: () => fetchData(true),
    flushNow: () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
      return flushChanges()
    },
    getAnchorId: (row) => rawData[row]?.id,
    resolveAnchorRow: (id) => {
      const idx = rawData.findIndex((r) => r.id === id)
      return idx >= 0 ? idx : undefined
    }
  })

  useEffect(() => {
    const subscription = cable.subscriptions.create(
      { channel: 'CapitalExpendituresChannel', valuation_id: valuationId },
      {
        connected() { setConnectionStatus('connected') },
        disconnected() { setConnectionStatus('disconnected') },
        received() {
          if (creatingRowsRef.current) return
          fetchData(true)
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [valuationId, fetchData, creatingRowsRef])

  useEffect(() => {
    if (readOnly) return
    const handler = async () => {
      try {
        const res = await fetch(createRowUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({ request_id: generateRequestId() })
        })
        const json = await res.json()
        if (json.success) {
          await fetchData(true)
          requestAnimationFrame(() => setTimeout(() => {
            const hot = hotRef.current?.hotInstance
            if (!hot) return
            hot.selectCell(hot.countRows() - 1, 0)
          }, 50))
        }
      } catch (e) {
        console.error('Failed to add capex project:', e)
      }
    }
    window.addEventListener('addCapexRow', handler)
    return () => window.removeEventListener('addCapexRow', handler)
  }, [readOnly, createRowUrl, csrfToken, fetchData])

  const tableData = useMemo(() => rawData.map((row) => ({ ...row })), [rawData])

  const flushChanges = useCallback(async () => {
    const pending = pendingRef.current
    if (pending.size === 0) return
    const items = []
    for (const [recordId, changedFields] of pending.entries()) {
      items.push({ id: recordId, changes: Object.fromEntries(changedFields) })
    }
    pendingRef.current = new Map()
    if (items.length === 0) return
    try {
      const res = await fetch(batchUpdateUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ items, request_id: generateRequestId() })
      })
      const json = await res.json()
      if (!json.success) {
        showError(t('save_error_invalid', translations))
        fetchData(true)
      }
    } catch (e) {
      console.error('Failed to batch update:', e)
      showError(t('save_error', translations))
      fetchData(true)
    }
  }, [batchUpdateUrl, csrfToken, fetchData, translations])

  const scheduledFlush = useCallback(() => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushTimeoutRef.current = setTimeout(() => flushChanges(), DEBOUNCE_DELAY)
  }, [flushChanges])

  useEffect(() => () => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushChanges()
  }, [flushChanges])

  const trackChange = useCallback((recordId, field, value) => {
    if (!pendingRef.current.has(recordId)) pendingRef.current.set(recordId, new Map())
    pendingRef.current.get(recordId).set(field, value)
  }, [])

  const handleDeleteRows = useCallback(async (rowIndices) => {
    const records = [...new Set(rowIndices)].map((i) => rawData[i]).filter(Boolean)
    if (records.length === 0) return
    const message = records.length > 1
      ? t('delete_confirm_many', translations, { count: records.length })
      : t('delete_confirm_one', translations, { name: records[0].name || t('delete_fallback_name', translations) })
    if (!confirm(message)) return
    try {
      const res = await fetch(batchDestroyUrl, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ ids: records.map((r) => r.id), request_id: generateRequestId() })
      })
      const json = await res.json()
      if (json.success) fetchData(true)
    } catch (e) {
      console.error('Failed to delete capex projects:', e)
    }
  }, [rawData, batchDestroyUrl, csrfToken, fetchData, translations])

  const saveSelectedTenants = useCallback((recordId, ids) => {
    setModalRow(null)
    const record = rawData.find((r) => r.id === recordId)
    if (!record) return
    setRawData((prev) => prev.map((r) => (r.id === recordId ? { ...r, selected_tenants: ids } : r)))
    trackChange(recordId, 'selected_tenants', ids)
    scheduledFlush()
  }, [rawData, trackChange, scheduledFlush])

  // Persist a scheduler save: store the scheduler object + chosen growth profile
  // and pin the amount to the entered total, clearing the timing fields the
  // scheduler overrides. Growth is applied only here (on Save) so cancelling the
  // modal never mutates the row.
  const saveScheduler = useCallback((recordId, scheduler, growthRate) => {
    setSchedulerRow(null)
    const patch = {
      scheduler,
      growth_rate: growthRate,
      amount: scheduler.totalEntered,
      amount_basis: 'ANNUAL',
      start_rule: 'VALUATION_DATE',
      start_offset_months: 0,
      specified_start_date: null,
      repeat: 'SINGLE_PAYMENT',
      end_rule: 'NONE',
      specified_end_date: null,
      duration_months: null
    }
    setRawData((prev) => prev.map((r) => (r.id === recordId ? { ...r, ...patch } : r)))
    Object.entries(patch).forEach(([f, v]) => trackChange(recordId, f, v))
    scheduledFlush()
  }, [trackChange, scheduledFlush])

  // Clear a schedule: disable it AND drop the amount the scheduler pinned, so the
  // line stops charging instead of resolving the old total through the normal path.
  const clearScheduler = useCallback((recordId) => {
    setSchedulerRow(null)
    const patch = { scheduler: { enabled: false }, amount: null }
    setRawData((prev) => prev.map((r) => (r.id === recordId ? { ...r, ...patch } : r)))
    Object.entries(patch).forEach(([f, v]) => trackChange(recordId, f, v))
    scheduledFlush()
  }, [trackChange, scheduledFlush])

  const growthSource = useMemo(
    () => growthProfileList.map((p) => p.name),
    [growthProfileList]
  )

  // EVA-266: grid group/column headers built from i18n (was a module-level const).
  const nestedHeaders = useMemo(() => [
    [
      { label: t('groups.project', translations), colspan: 2 },
      { label: t('groups.calculation', translations), colspan: 3 },
      { label: t('groups.application', translations), colspan: 2 },
      { label: t('groups.timing', translations), colspan: 7 },
      { label: t('groups.growth', translations), colspan: 1 }
    ],
    [
      t('columns.project_name', translations), t('columns.capex_type', translations),
      t('columns.input_method', translations), t('columns.amount', translations),
      t('columns.amount_basis', translations), t('columns.calculation_scope', translations),
      t('columns.selected_tenants', translations),
      t('columns.start_rule', translations), t('columns.specified_start_date', translations),
      t('columns.start_offset_months', translations), t('columns.repeat', translations),
      t('columns.end_rule', translations), t('columns.specified_end_date', translations),
      t('columns.duration_months', translations), t('columns.growth_rate', translations)
    ]
  ], [translations])

  const hotSettings = useMemo(() => ({
    data: tableData,
    columns: COLUMNS.map((c) => {
      if (c.data === 'growth_rate') return { ...c, source: growthSource }
      // Enum dropdowns: source shows labels, renderer maps the stored code → label.
      if (enums.isEnumColumn(c.data)) return { ...c, source: enums.labelsFor(c.data), renderer: enums.renderer(c.data) }
      return c
    }),
    nestedHeaders,
    rowHeaders: true,
    fixedColumnsStart: 1,
    themeName: 'ht-theme-horizon',
    licenseKey: HOT_LICENSE_KEY,
    sanitizer: sanitizeHandsontableHtml,
    width: '100%',
    height: hotHeight || undefined,
    manualColumnResize: true,
    // EVA-215: let dropdown popups grow to their content width instead of being
    // trimmed to the (narrow) column width, so long labels like "Amount per
    // Rentable Area" / "% Rental Revenue before Rent Free" aren't clipped to "…".
    trimDropdown: false,
    contextMenu: readOnly ? false : {
      items: {
        delete_row_custom: {
          name: t('delete_rows', translations),
          callback(_key, selection) {
            const rows = new Set()
            selection.forEach((range) => {
              const start = Math.min(range.start.row, range.end.row)
              const end = Math.max(range.start.row, range.end.row)
              for (let r = start; r <= end; r += 1) rows.add(r)
            })
            if (rows.size) handleDeleteRows([...rows])
          }
        }
      }
    },
    cells(row, col) {
      const cellProps = {}
      const record = rawData[row]
      const field = COLUMNS[col]?.data
      if (readOnly) { cellProps.readOnly = true; return cellProps }
      if (record && field && isFieldDisabled(field, record)) {
        cellProps.readOnly = true
        cellProps.className = 'opex-cell-disabled'
      }
      if (record && record.calculation_scope === 'PROPERTY_LEVEL') {
        if (field === 'start_rule') cellProps.source = enums.labelsOf('start_rule', START_RULES.filter((r) => !TENANT_START_RULES.includes(r)))
        if (field === 'end_rule') cellProps.source = enums.labelsOf('end_rule', END_RULES.filter((r) => r !== 'LEASE_EXPIRY'))
      }
      if (field === 'selected_tenants') {
        cellProps.readOnly = true
        cellProps.renderer = (instance, td) => {
          const ids = (record?.selected_tenants || []).map(Number)
          const names = ids.map((id) => tenantLabels.get(id)).filter(Boolean)
          const shown = names.slice(0, 4).join(', ')
          td.textContent = names.length > 4 ? `${shown} +${names.length - 4}` : shown
          if (record && record.calculation_scope !== 'SELECTED_TENANTS') td.style.color = '#bbb'
        }
      }
      // Amount cell: Fixed Amount rows can open the scheduler from a chip. A
      // scheduled row shows its total read-only; otherwise the chip sits beside
      // the editable numeric value.
      if (field === 'amount' && record && record.input_method === 'FIXED_AMOUNT') {
        const scheduled = isSchedulerEnabled(record)
        if (scheduled) cellProps.readOnly = true
        cellProps.renderer = (instance, td, r2, c2) => {
          td.innerHTML = ''
          td.className = 'htRight'
          // HOT 18 passes the Intl-formatted display string to custom
          // renderers. Read the source value so grouping separators never
          // turn Number(value) into NaN.
          const rawValue = instance.getSourceDataAtCell(instance.toPhysicalRow(r2), c2)
          const span = instance.rootDocument.createElement('span')
          span.textContent = (rawValue === null || rawValue === undefined || rawValue === '')
            ? '' : Number(rawValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          td.appendChild(span)
          const chip = instance.rootDocument.createElement('span')
          chip.className = 'capex-schedule-chip'
          chip.textContent = scheduled ? '📅 Scheduled' : '📅 Schedule'
          chip.style.cssText = 'margin-left:6px;cursor:pointer;color:#0d6efd;font-size:11px;white-space:nowrap'
          td.appendChild(chip)
        }
      }
      if (record && (field === 'growth_rate' || field === 'amount_basis') && PERCENT_METHODS.includes(record.input_method)) {
        cellProps.renderer = (instance, td) => {
          td.textContent = ''
          td.classList.add('opex-cell-disabled')
        }
      }
      return cellProps
    },
    beforePaste: handleBeforePaste,
    afterOnCellMouseDown(event, coords) {
      if (readOnly) return
      const record = rawData[coords.row]
      if (!record) return
      if (coords.col === SELECTED_TENANTS_COL && record.calculation_scope === 'SELECTED_TENANTS') {
        setModalRow(record.id)
        return
      }
      // Open the scheduler when the chip inside a Fixed Amount cell is clicked.
      if (coords.col === AMOUNT_COL && record.input_method === 'FIXED_AMOUNT' &&
          event?.target?.classList?.contains('capex-schedule-chip')) {
        setSchedulerRow(record.id)
      }
    },
    afterChange(changes, source) {
      if (!changes || source === 'loadData' || source === 'server' || readOnly) return
      setRawData((prev) => {
        const next = prev.map((r) => ({ ...r }))
        changes.forEach(([row, prop, _old, newVal]) => {
          const record = next[row]
          if (!record || typeof prop !== 'string' || prop === 'selected_tenants') return
          let value = newVal === '' || newVal === undefined ? null : newVal
          // Enum dropdowns edit as labels; convert back to the stored code.
          if (enums.isEnumColumn(prop) && value !== null) value = enums.codeFor(prop, value)
          if (NUMERIC_FIELDS.has(prop) && value !== null) {
            const n = parseNumericInput(value)
            value = Number.isFinite(n) ? n : null
          }
          record[prop] = value
          trackChange(record.id, prop, value)
          // A scheduler is only valid for Fixed Amount — clear it when the method changes away.
          if (prop === 'input_method' && value !== 'FIXED_AMOUNT' && isSchedulerEnabled(record)) {
            record.scheduler = { enabled: false }
            trackChange(record.id, 'scheduler', { enabled: false })
          }
          Object.entries(sanitizeRecord(record)).forEach(([f, v]) => trackChange(record.id, f, v))
        })
        return next
      })
      scheduledFlush()
    },
    afterDocumentKeyDown(event) {
      if (readOnly) return
      handleHotKeyboardShortcut(event, {
        onAddRow: () => window.dispatchEvent(new CustomEvent('addCapexRow')),
        onDeleteRow: () => {
          const hot = hotRef.current?.hotInstance
          const selected = hot?.getSelectedRangeLast()
          if (!selected) return
          const start = Math.min(selected.from.row, selected.to.row)
          const end = Math.max(selected.from.row, selected.to.row)
          const rows = []
          for (let r = start; r <= end; r += 1) rows.push(r)
          handleDeleteRows(rows)
        }
      })
    }
  }), [tableData, readOnly, rawData, tenantLabels, growthSource, enums, handleBeforePaste, handleDeleteRows, scheduledFlush, trackChange, hotHeight, nestedHeaders, translations])

  useEffect(() => {
    if (typeof window !== 'undefined' && !loading) window.__evaCapitalExpendituresHot = hotRef.current?.hotInstance
    return () => { if (typeof window !== 'undefined') delete window.__evaCapitalExpendituresHot }
  }, [loading])

  const schedulerRecord = schedulerRow !== null ? rawData.find((r) => r.id === schedulerRow) : null

  return (
    <div className="capital-expenditure-table-container">
      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading', translations)}</span>
          </div>
        </div>
      ) : (
        <div ref={viewportRef} style={{ height: '50vh', maxWidth: '100%' }}>
          <HotTable ref={hotRef} {...hotSettings} />
        </div>
      )}

      {modalRow !== null && (
        <TenantSelectorModal
          tenants={tenants}
          selectedIds={rawData.find((r) => r.id === modalRow)?.selected_tenants || []}
          onSave={(ids) => saveSelectedTenants(modalRow, ids)}
          onClose={() => setModalRow(null)}
          translations={translations}
        />
      )}

      {schedulerRecord && (
        <CapexSchedulerModal
          row={schedulerRecord}
          years={schedulerYears}
          valuationDate={valuationDate}
          growthProfiles={growthProfileList}
          onSave={(scheduler, growthRate) => saveScheduler(schedulerRecord.id, scheduler, growthRate)}
          onClear={() => clearScheduler(schedulerRecord.id)}
          onCancel={() => setSchedulerRow(null)}
        />
      )}

      <ConnectionStatus
        status={connectionStatus}
        mountEl={connectionMountEl}
        labels={{
          connected: t('connection.live', translations),
          disconnected: t('connection.disconnected', translations),
          connecting: t('connection.connecting', translations),
        }}
      />
    </div>
  )
}
